/**
 * Copy for the five core feature pages, the All-features list, the Property
 * managers and Research landings, and the Homepage v2 FAQ (build Appendix
 * A1, A6–A8). One module so the pages, `llms-full.txt` and the structured
 * data all say the same thing.
 *
 * Where a line in the build file failed the code check (guardrail 7) or a
 * later backend decision (D13–D27), the text here is the corrected version;
 * every change is logged in docs/build/copy-changes.md.
 */
import {
  HOST_BASE_PRICE,
  OWNER_PRICE,
  TIER_STEPS_SHORT,
  FREE_RESEARCH_SEARCHES,
  PAUSED_RETENTION_DAYS,
  TRIAL_DAYS,
  formatUsd,
} from "@/lib/marketing/pricing-copy";
import { hostMonthlyCents, ownerPortalMonthlyCents } from "@shared/pricing";
import { marketingTitle } from "@/data/marketing-seo";

export type CorePageSlug = "messages" | "maintenance" | "locks" | "leasing" | "numbers";

export interface CoreBlock {
  title: string;
  body: string;
}

export interface CorePage {
  slug: CorePageSlug;
  path: string;
  /** Short name for cards, breadcrumbs and links ("Messages"). */
  name: string;
  eyebrow: string;
  h1: string;
  sub: string;
  blocks: CoreBlock[];
  quote: string;
  proofLine?: string;
  note?: string;
  metaTitle: string;
  metaDescription: string;
}

/** Attribution under every core-page quote (A6). */
export const QUOTE_ATTRIBUTION = "— Travis Schwartz, PadSplit host and founder of PadPulse";

export const CORE_PAGES: CorePage[] = [
  {
    slug: "messages",
    path: "/features/messages",
    name: "Messages",
    eyebrow: "Messages",
    h1: "Never lose a member message again.",
    sub: "PadSplit marks a message read the moment you open it on your phone. PadPulse puts every member message from every house in one team inbox, so nothing slips.",
    blocks: [
      {
        title: "Team inbox synced with PadSplit.",
        body: "Assign a thread, mark it resolved, @mention a teammate. Sync timing varies; 60s is the send/reconciliation cycle, not a guaranteed arrival time. Incoming messages depend on PadSplit harvesting and can take longer.",
      },
      {
        title: "Answer faster.",
        body: "Saved and scheduled replies and broadcasts to one house or your whole portfolio. AI-drafted replies require a beta grant. Continuous Auto Draft requires a beta grant and explicit enabling; signup or a paid plan alone does not grant it. Auto-send defaults to off, emergencies need a person, and quiet hours hold automatic sends.",
      },
      {
        title: "The whole picture beside the thread.",
        body: "Member, vehicle and lock details next to every conversation.",
      },
      { title: "Set up in minutes.", body: "Connect your PadSplit account in about 3 minutes." },
    ],
    quote:
      "If you click on a message on PadSplit, it marks it as read right away… now it falls through the cracks, especially at scale.",
    note: "Rooms off PadSplit? Members message you through your own PadPulse member portal.",
    metaTitle: marketingTitle("Team inbox for PadSplit messages"),
    metaDescription:
       "PadSplit member messages in one team inbox. Assign, resolve and answer faster. AI-drafted replies require a beta grant; Continuous Auto Draft also requires explicit enabling.",
  },
  {
    slug: "maintenance",
    path: "/features/maintenance",
    name: "Maintenance",
    eyebrow: "Maintenance",
    h1: "Maintenance with a clear next step.",
    sub: "Turn requests into tasks, dispatch the right vendor, review photo proof, and approve the work.",
    blocks: [
      {
        title: "Requests become tasks.",
        body: "Each request lands as a task with the house and room attached, and a service form submission becomes a task in one click.",
      },
      {
        title: "Vendors who prove the work.",
        body: "Dispatch from the task. Vendors get their own portal with a per-job timer, and location-checked service forms with photos show the work was done on site.",
      },
      {
        title: "Approve and pay in one place.",
        body: "Approve the job and pay the vendor through Stripe payouts.",
      },
      {
        title: "Supplies on schedule.",
        body: "Track supply orders and set reorders.",
      },
      {
        title: "Cleaner houses, less effort.",
        body: "Recurring cleaning schedules for every house, by member, with form submissions to tie it all together.",
      },
    ],
    quote:
      "Somebody was sending me old pictures… I only found out because the city called me and said, 'your grass is 12 inches tall.'",
    metaTitle: marketingTitle("PadSplit maintenance and vendor management"),
    metaDescription:
      "Maintenance requests turn into tasks, vendors send photo proof from location-checked forms, and you approve and pay in one place.",
  },
  {
    slug: "locks",
    path: "/features/locks",
    name: "Locks and thermostats",
    eyebrow: "Locks and thermostats",
    h1: "Locks and thermostats in one place.",
    sub: "Manage every door code from one place, and hear about a dying battery before it becomes a lockout.",
    blocks: [
      {
        title: "Every code in one place.",
        body: "Create, change and remove member and vendor codes without logging into each lock app.",
      },
      {
        title: "Battery alerts before a lockout.",
        body: "Know which lock needs attention this week.",
      },
      {
        title: "Thermostats that stay in range.",
        body: "Out-of-range temperature alerts for every house.",
      },
      {
        title: "TTLock and Sifely included.",
        body: "Other lock brands connect through Seam for $2 a lock a month.",
      },
    ],
    quote: "'Hey, it's hot in here.'… 'Crap, it's 80°. It's supposed to be 74.'",
    metaTitle: marketingTitle("Smart locks and thermostats for PadSplit houses"),
    metaDescription:
      "Every door code in one place, battery alerts before lockouts, and out-of-range thermostat alerts. TTLock and Sifely included.",
  },
  {
    slug: "leasing",
    path: "/features/leasing",
    name: "Leasing",
    eyebrow: "Leasing",
    h1: "Fill rooms with the right members.",
    sub: "Every booking request from every house in one queue, screened with your own booking form and priced by your own rules.",
    blocks: [
      {
        title: "One queue for every request.",
        body: "See every booking request across your portfolio in one place.",
      },
      {
        title: "Your own booking form, requested instantly.",
        body: "Conditional questions, a short video, a signature, and ID capture.",
      },
      {
        title: "RapidFill pricing.",
        body: "Rules that adjust rent on rooms that sit empty.",
      },
      {
        title: "Your own direct-booking site.",
        body: "A branded booking site for your rooms, plus a tracker for your third-party listings.",
      },
    ],
    quote: "One of the biggest things is just professionalism and trust. That's where you lose people.",
    metaTitle: marketingTitle("PadSplit booking requests and room pricing"),
    metaDescription:
      "One queue for every booking request, your own screening form, rules-based room pricing, and a direct-booking site for your rooms.",
  },
  {
    slug: "numbers",
    path: "/features/numbers",
    name: "Numbers",
    eyebrow: "Numbers",
    h1: "Know your numbers without the month-end scramble.",
    sub: "Review imported PadSplit earnings and linked bank transactions in a P&L for every property.",
    blocks: [
      { title: "PadSplit earnings in your P&L.", body: "Import your PadSplit financials and review the available periods. Import completion and data availability vary; this is not a guaranteed daily feed." },
      {
        title: "Link a supported bank account.",
        body: "Read-only, Stripe-hosted bank linking is available for supported U.S. financial institutions and USD accounts; not every bank or account is guaranteed to connect. Transactions feed your property P&L, not subscription payments. Review imported entries and categories before relying on the P&L. Test money is excluded.",
      },
      { title: "A P&L per property.", body: "Ready for your bookkeeper whenever they ask." },
    ],
    quote: "Download your statements and give them to your bookkeeper every month. Super annoying.",
    metaTitle: marketingTitle("Live P&L for PadSplit houses"),
    metaDescription:
       "Review imported PadSplit earnings and transactions from supported U.S. bank accounts in a property P&L. Read-only bank linking; review entries before relying on reports.",
  },
];

export function getCorePage(slug: string): CorePage | undefined {
  return CORE_PAGES.find((p) => p.slug === slug);
}

/** Homepage v2 section 4: the five outcomes (each links to its core page) plus the team card. */
export const OUTCOME_CARDS: Array<{ title: string; body: string; href: string }> = [
  { title: "Never lose a message.", body: "Team inbox synced with PadSplit. AI-drafted replies require a beta grant; Continuous Auto Draft also requires explicit enabling.", href: "/features/messages" },
  { title: "Maintenance with a clear next step.", body: "Create tasks, dispatch vendors, review photo proof and approve work.", href: "/features/maintenance" },
  { title: "Manage your locks.", body: "Control codes and review battery and thermostat alerts.", href: "/features/locks" },
  {
    title: "Fill rooms with the right members.",
    body: "Booking requests, your own forms, RapidFill pricing, a direct-booking site.",
    href: "/features/leasing",
  },
  { title: "Know your numbers.", body: "Imported PadSplit financials and linked bank transactions, organized by property.", href: "/features/numbers" },
  { title: "Your whole team.", body: "Roles, permissions and unlimited users.", href: "/features" },
];

/** A7: the searchable list, grouped. Only features the audit and Travis's calls keep. */
export const ALL_FEATURE_GROUPS: Array<{ name: string; items: Array<{ name: string; href?: string; badge?: string }> }> = [
  {
    name: "Team and members",
    items: [{ name: "Members and vehicles" }, { name: "Roles and permissions" }, { name: "Unlimited users" }],
  },
  {
    name: "Leasing extras",
    items: [
      { name: "Direct-booking site", href: "/features/leasing" },
      { name: "Third-party listing management", href: "/features/leasing" },
      { name: "Room image staging generator" },
    ],
  },
  {
    name: "Property managers",
    items: [
      { name: "Owner portal", href: "/property-managers" },
      { name: "Monthly owner statements", href: "/property-managers" },
    ],
  },
  {
    name: "Research",
    items: [
      { name: "Market research by city", href: "/research" },
      { name: "Buy box search", href: "/research" },
      { name: "Daily buy box alerts", href: "/research" },
      { name: "Underwriting with PDF export", href: "/research" },
      { name: "Lender directory", href: "/research" },
      { name: "Realtor directory", href: "/research" },
      { name: "Savings calculator", href: "/savings-calculator" },
    ],
  },
  {
    name: "Maintenance",
    items: [{ name: "Cleaning schedules by member, with form submissions", href: "/features/maintenance" }],
  },
  {
    name: "Setting up a house",
    items: [{ name: "Recommended Furnishings", href: "/recommendedfurnishings" }],
  },
  {
    name: "Off PadSplit",
    items: [{ name: "Member portal" }],
  },
];

/** A8 /property-managers. */
const PM_EXAMPLE_PROPERTIES = 50;
const PM_EXAMPLE_OWNERS = 30;
export const PROPERTY_MANAGERS_PAGE = {
  h1: "Run PadSplit houses for owners without the spreadsheets.",
  sub: "Everything in Host, plus an owner portal with monthly owner statements.",
  priceLine: `${HOST_BASE_PRICE} a property, ${TIER_STEPS_SHORT}, plus ${OWNER_PRICE} per owner a month. Example: ${PM_EXAMPLE_PROPERTIES} properties and ${PM_EXAMPLE_OWNERS} owners is ${formatUsd(
    hostMonthlyCents(PM_EXAMPLE_PROPERTIES) + ownerPortalMonthlyCents(PM_EXAMPLE_OWNERS),
  )} a month.`,
  blocks: [
    { title: "Owner portal.", body: "Owners see their properties and statements without emailing you." },
    { title: "Monthly owner statements.", body: "Statements for every owner from the same place you run the houses." },
    { title: "Your whole team.", body: "Unlimited users with roles and permissions." },
  ] as CoreBlock[],
  bookCallUrl: "https://calendar.app.google/rdmXfD8FR7yczsmU7",
  metaTitle: marketingTitle("Software for PadSplit property managers"),
  metaDescription: `Everything in Host plus an owner portal with monthly owner statements. ${HOST_BASE_PRICE} a property, plus ${OWNER_PRICE} per owner a month. ${TRIAL_DAYS}-day free trial, no card.`,
};

/** A8 /research. */
export const RESEARCH_PAGE = {
  h1: "Free research tools for your first co-living house.",
  sub: `Size up a market, set your buy box and underwrite a house before you buy. Free, ${FREE_RESEARCH_SEARCHES} buy box searches a month.`,
  blocks: [
    { title: "Market research by city.", body: "" },
    { title: "Buy box search.", body: "" },
    { title: "Daily buy box agents.", body: "Set up to 3 daily buy box agents and get the houses that fit every morning. Included with Host." },
    { title: "Underwriting with PDF export.", body: "" },
    { title: "Lenders and realtors who know co-living.", body: "" },
    { title: "Savings calculator", body: "", href: "/savings-calculator" },
  ] as Array<CoreBlock & { href?: string }>,
  metaTitle: marketingTitle("Free co-living research tools"),
  metaDescription: `Market research by city, buy box search and underwriting with PDF export. Free, ${FREE_RESEARCH_SEARCHES} buy box searches a month.`,
};

/** A1 section 7 FAQ (Homepage v2). */
export const HOME_FAQ: Array<{ question: string; answer: string }> = [
  {
    question: "How does PadPulse connect to PadSplit?",
    answer:
      "Create an account, then connect your PadSplit host account from the Connect PadSplit screen. The connection step takes about 3 minutes; full setup and imports can take longer. Member and message sync timing varies, and financials use the available imported periods.",
  },
  {
    question: "Is PadPulse part of PadSplit?",
    answer: "No. PadPulse is independent software built by a PadSplit host. It connects to your PadSplit account.",
  },
  { question: "What counts as a property?", answer: "One address, on or off PadSplit, with any number of rooms." },
  {
    question: "Do I need to be on PadSplit?",
    answer:
      "No. Rooms off PadSplit get your own direct-booking site, your own booking form, locks, maintenance and the P&L. For rooms off PadSplit, your own member portal handles messages and booking requests.",
  },
  {
    question: "How long does setup take?",
    answer: "About 3 minutes for the PadSplit connection step, not full onboarding or completed imports. Add devices, vendors and a supported bank connection later when you're ready.",
  },
  {
    question: "Does signup include continuous AI drafting?",
    answer: "AI-drafted replies require a beta grant. Continuous Auto Draft requires a beta grant and explicit enabling; signup or a paid plan alone does not grant it. Auto-send is off by default, emergencies need a person, and quiet hours hold automatic sends. The member portal is already released, not beta.",
  },
  {
    question: "Which banks connect today?",
    answer: "PadPulse offers read-only, Stripe-hosted bank linking for supported U.S. financial institutions and USD accounts. Not every bank or account is guaranteed to connect. Transaction feeds support your P&L, not subscription payments; review entries before relying on reports, and test money is excluded.",
  },
  {
    question: "Can my team or property manager use it?",
    answer: `Yes. Users are unlimited, with roles and permissions. Property managers also get an owner portal with monthly owner statements for ${OWNER_PRICE} per owner a month.`,
  },
  {
    question: "What happens after the trial?",
    answer: `Add a card anytime to keep going; monthly plans cancel anytime. If you don't add a card, your account pauses as read-only and we keep your data for ${PAUSED_RETENTION_DAYS} days.`,
  },
];

/** A1 section 6. */
export const COMPARISON_ROWS: Array<{ job: string; padsplit: string; padpulse: string }> = [
  { job: "Member messages", padsplit: "Messenger", padpulse: "Team inbox: assign, resolve, @mention" },
  { job: "Smart locks", padsplit: "Manual entry", padpulse: "Every code in one place, battery alerts" },
  { job: "Repricing", padsplit: "Manual prices and promos", padpulse: "Your own rules for rooms that sit empty" },
  { job: "Booking-request screening", padsplit: "Manual request on a 24-hour ticking clock", padpulse: "Your own form, requested instantly" },
  { job: "P&L", padsplit: "Income earnings by export", padpulse: "Imported PadSplit financials and read-only transactions from supported U.S. bank accounts; review before relying on reports" },
  { job: "Maintenance", padsplit: "Maintenance tickets", padpulse: "Request to task to vendor, with photo proof" },
];
